// CustomSignInPage.tsx

import React, { useEffect, useRef } from "react";
import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  Container,
  ThemeProvider,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import logo from "./logo.png";
import { useNavigate } from "react-router-dom";
import { axiosRequest, fetchCsrfToken } from "../../service/AxiosService";
import { useUser } from "../../context/UserContext";
import theme from "../../style/theme";
import { operationLog } from "../../utils/OperationLogUtil";

/**
 * カスタムサインインページコンポーネント
 * - メールアドレスとパスワードによるログイン機能を提供する
 * - CSRFトークンの取得とログイン後のユーザー情報取得を行う
 * - Microsoftアカウント（SSO）によるログインも提供する
 */
function CustomSignInPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saveLoginInfo, setSaveLoginInfo] = useState(false);
  const [error, setError] = useState("");
  const { setUser, fetchUser } = useUser();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };


  // ログインモード（null=判定中 / true=保守モード / false=通常モード）
  const [maintenanceMode, setMaintenanceMode] = useState<boolean | null>(null);
  // 設定取得失敗（=ログイン不可。fail-closed）
  const [configError, setConfigError] = useState(false);
  // 認証エラーで戻された場合（/login?error=...）は自動遷移を止める（無限ループ防止）
  const authError = new URLSearchParams(window.location.search).has("error");
  // 自動遷移の多重発火防止（React StrictModeのeffect二重実行対策）
  const ssoRedirectStarted = useRef(false);
  // SSO認証への遷移中フラグ。
  const [ssoRedirecting, setSsoRedirecting] = useState(false);

  interface initializeResponce {
    success: boolean;
  }
  const handleInitialize = async (): Promise<boolean> => {
    try {
      const data = await axiosRequest<initializeResponce>(
        "/loginPage/initialize",
        "POST"
      );
      if (data!.success) {
        navigate("/");
        return true;
      }
    } catch (error) {
      console.log(error);
    }
    return false;
  };

  useEffect(() => {
    const init = async () => {
      const loggedIn = await handleInitialize();
      sessionStorage.clear(); // sessionStorageにあるデータをクリアする
      if (loggedIn) return;

      // サーバー設定からログインモードを取得
      try {
        const config = await axiosRequest<{ maintenanceMode: boolean }>(
          "/config",
          "GET"
        );
        if (config?.maintenanceMode === undefined) {
          throw new Error("設定を取得できませんでした");
        }
        const isMaintenance = config.maintenanceMode;
        setMaintenanceMode(isMaintenance);
        // 通常モード：ボタン操作なしでSSO認証へ自動遷移する（多重発火は抑止）
        if (!isMaintenance && !authError && !ssoRedirectStarted.current) {
          ssoRedirectStarted.current = true;
          ssoLogin();
        }
      } catch (error) {
        // 設定取得失敗＝ログイン不可（fail-closed）。フォームもSSOも表示しない
        console.log(error);
        setConfigError(true);
      }
    };
    init();
    // マウント時に1回だけ実行する初期化処理（ログイン確認→モード取得→自動遷移）のため、
    // 依存配列は意図的に空とする。ssoLogin等を依存に加えると再レンダリングのたびに
    // 初期化が再実行され、多重リクエスト・多重リダイレクトの原因となる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    setError(""); // エラーリセット
    try {
      // パラメータの設定
      const options = {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        data: new URLSearchParams(
          saveLoginInfo
            ? { username: email, password: password, "remember-me": "on" }
            : { username: email, password: password }
        ),
      };

      // サーバー通信処理
      const response = await axiosRequest<boolean>("/login", "POST", options);

      if (!response) {
        throw new Error("ログインに失敗しました");
      }
      // 🔽 ログイン後にCSRFトークンを取得
      await fetchCsrfToken();
      await fetchUser();

      // 操作ログ保存処理
      await operationLog("ログイン画面", "ログイン処理", "成功");

      // 認証成功後、適切なページへ遷移
      navigate("/");
    } catch (err) {
      setError("メールアドレスまたはパスワードが間違っています");
    }
  };

  // SSO（Microsoftアカウント）ログイン処理
  // 認可URLの組立はバックエンド（/api/auth/redirect）で行う。
  // identity_provider指定によりCognitoのログイン画面を経由せず直接Entra認証へ遷移する。
  const ssoLogin = () => {
    setSsoRedirecting(true);
    window.location.href = `${process.env.REACT_APP_BASE_URL}auth/redirect`;
  };

  // 設定取得失敗時はログイン不可（fail-closed）
  if (configError) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          sx={{ gap: 2, mt: 12 }}
        >
          <Typography>
            現在ログインできません。しばらくしてから再度お試しください。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            問題が解消しない場合はシステム管理者へお問い合わせください。
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // SSO認証へ遷移中はモード共通の案内を表示する
  // （保守モードでボタン押下後、ブラウザが遷移を完了するまでフォームが残らないようにする）
  if (ssoRedirecting) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          sx={{ gap: 2, mt: 12 }}
        >
          <Typography>Microsoft認証ページへ移動しています…</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // 通常モード（またはモード判定中）はログインフォームを表示しない
  if (maintenanceMode !== true) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          sx={{ gap: 2, mt: 12 }}
        >
          {authError ? (
            <>
              <Typography>認証処理でエラーが発生しました。</Typography>
              <Button variant="contained" onClick={ssoLogin}>
                再度ログインする
              </Button>
            </>
          ) : maintenanceMode === false ? (
            <Typography>Microsoft認証ページへ移動しています…</Typography>
          ) : null /* モード判定中は何も表示しない */}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <Container
          maxWidth="sm"
          sx={{ paddingLeft: 2, paddingRight: 2 }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="row"
            sx={{ gap: 2, my: 2 }}
          >
            <img src={logo} width="3%" height="auto" />
            <Typography variant="h2">統合ワークフロー</Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="row"
            sx={{ gap: 2, my: 2 }}
          >
            <Typography variant="h3">
              ようこそ、続行するためにサインインしてください
            </Typography>
          </Box>
          {error && <Typography color="error">{error}</Typography>}
          <Box sx={{ my: 2 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              <TextField
                name="username"
                label="メールアドレス"
                type="email"
                fullWidth
                required
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel htmlFor="password">パスワード</InputLabel>
                <OutlinedInput
                  name="password"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="パスワード"
                />
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveLoginInfo}
                    onChange={(event) =>
                      setSaveLoginInfo(event.currentTarget.checked)
                    }
                  />
                }
                label="ログイン情報を記憶する"
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
              >
                ログイン
              </Button>
            </form>
          </Box>

          <div>
            <a href="/">パスワードを忘れた場合</a>
          </div>

          {/* SSO（Microsoftアカウント）ログイン */}
          <Divider sx={{ my: 4 }}>
            <Typography variant="body2">または</Typography>
          </Divider>
          <Box display="flex" justifyContent="center">
            <Button
              variant="outlined"
              fullWidth
              onClick={ssoLogin}
              sx={{ textTransform: "none" }}
            >
              Microsoftアカウントでログイン
            </Button>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default CustomSignInPage;
